import { AfterViewInit, ChangeDetectorRef, Component, effect, ElementRef, inject, Input, OnInit, signal, SimpleChanges, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { ChatService } from '../../../services/chat.service';
import { CommonModule } from '@angular/common';

import { Firestore, collection, doc, getDoc, getDocs, onSnapshot, query, setDoc, updateDoc, where } from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { LoaderComponent, LoaderService, ToastService } from '../../../shared/loader';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {MatSnackBar} from '@angular/material/snack-bar';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { UserService } from '../../../components/login/user.service';
import { Conversation } from '../../../components/login/user.interface';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { marked } from 'marked';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EmailService } from '../../../services/email.service';
import { applyBrandColors } from '../../../config/apply-brand-colors';
import { MatCardModule } from '@angular/material/card';

@Component({
  standalone: true,
  selector: 'chat-widget',
  imports: [
    CommonModule, 
    FormsModule, 
    MatButtonModule,
    RouterModule,
    HttpClientModule,
    MatFormFieldModule, 
    MatInputModule,
    LoaderComponent,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule,
    MatCardModule
],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
})
export class BeeasyChatComponent implements OnInit, AfterViewInit{
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('representativeDialog') representativeDialog!: TemplateRef<any>;
  @ViewChild('chatContainerSpace') chatContainerSpace!: ElementRef;

  private destroy = new Subject<boolean>();
  isExpanded: Record<string, boolean> = {};

  // activatedRoute = inject(ActivatedRoute);
  // router = inject(Router);
  emailService = inject(EmailService);
  private snackBar = inject(MatSnackBar);
  firestore = inject(Firestore);
  auth = inject(Auth);
  chatService = inject(ChatService);
  toastService = inject(ToastService);
  loaderService = inject(LoaderService);
  userService = inject(UserService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  @Input() workSpaceClientId: string | null = null;
  @Input() data: string | null = null;
  @Input() isTestMode: boolean = false;
  @Input() clearTrigger: number = 0;

  helpButtonText: string | null = null;
  talkToAdmin: string | null = null;

  humanRequest = signal(false);
  isHumanChat = signal(false);
  currentAssigned = signal<string | null>(null);

  showHelpButton = signal(true);
  loader = false;
  showNoUserMsg = signal(false);
  disabledInput = signal(true);
  currentResponse = signal('');
  isTyping = signal(false);
  chatHistory = signal<{ role: 'user' | 'assistant', content: string, htmlContent: SafeHtml,  tokens: any, productos: any[] }[]>([]);
  workSpaceId: string | null = null;
  userQuery = '';
  userEmailOrPhone = '';
  userIdentified = signal(false);
  isChatSpace = signal(false);

  conversationId: string = '';
  isOpen = false;
  userConfig = {
    defaultTone: '',
    rolePrompt: '',
    defaultInstructions: [] as string[],
    integration: ''
  };
  unsubscribeConversationListener: (() => void) | null = null;
  private unsubscribeChat: (() => void) | null = null;
  emails = [];

  private LOCAL_KEY = 'chat_local_history';
  private LOCAL_TTL = 48 * 60 * 60 * 1000; // 48 horas

  constructor(private cd: ChangeDetectorRef, private elRef: ElementRef, private dialog: MatDialog, private sanitizer: DomSanitizer) {
    effect(() => {
      const history = this.chatHistory();
      const workSpaceId = this.workSpaceClientId;
      const conversationId = this.conversationId;

      if (!workSpaceId || !conversationId || history.length === 0) return;

      // Evitamos guardar si los mensajes no cambiaron
      const plainHistory = history.map((m) => ({
        role: m.role,
        content: m.content,
        tokens: m.tokens,
        productos: m.productos,
      }));


      if(!this.isTestMode) {
        localStorage.setItem(this.LOCAL_KEY, JSON.stringify({
          timestamp: Date.now(),
          email: this.userEmailOrPhone,
          // messages: plainHistory,
          conversationId,
          // humanRequest: this.humanRequest(),
          // assignedTo: this.currentAssigned
        }));

        this.saveConversationToFirestoreInline(conversationId, plainHistory);
      }

    });

    this.loaderService.loaderResponse$
      .pipe(takeUntil(this.destroy))
      .subscribe((status: any) => {
        this.loader = status;
    });
  }

  async ngOnInit() {
    const container = document.querySelector('.chat-container-all');
    container?.classList.add('hidden-until-theme');
    
    this.ensureMaterialIconsLoaded();

    this.getUserIpAndLocation();

    this.updateChatHeight();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        this.updateChatHeight();
      });
    }
  }

  updateChatHeight() {
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    document.documentElement.style.setProperty('--chat-height', `${viewportHeight}px`);
  }

  async ngAfterViewInit() {
    if(!this.isTestMode) {
      const url = await this.router.url;
      if (url.startsWith('/empresa/')) {
        this.isChatSpace.set(true);
        const params = await firstValueFrom(this.route.paramMap);
        const slug = params.get('slug');

        if (!slug) {
          this.toastService.showError('No se encontró la empresa ' + slug);
          return;
        }

        const publicLinksRef = collection(this.firestore, 'chatSpaces');
        const q = query(publicLinksRef, where('slug', '==', slug));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          this.toastService.showError('No se encontró la empresa ' + slug);
          return;
        }

        const data = snapshot.docs[0].data() as any;
        this.workSpaceClientId = data.workspaceId;
      }

      const local = await this.loadFromLocalStorage();

      if(local) {
        await this.listenToChatHistory();
      }
      } else {
        this.userEmailOrPhone = 'Prueba';
        this.userIdentified.set(true);
      }

      const attrworkSpaceClientId = this.elRef.nativeElement.getAttribute('workSpaceClientId');
      const attrworData = this.elRef.nativeElement.getAttribute('data');

      this.workSpaceClientId = this.workSpaceClientId ? this.workSpaceClientId : attrworkSpaceClientId;
      this.data = this.data ? this.data : attrworData;


    if (!attrworkSpaceClientId && !this.workSpaceClientId) {
      this.showNoUserMsg.set(true);
      this.toastService.showError('No hay espacio de trabajo.')
      return;
    }
    
    if(this.data && this.data === 'woocommerce') {
      this.userConfig.integration = 'woo';
    }

    const newRandomChat = crypto.randomUUID();
    if(this.conversationId === '') {
      this.conversationId = newRandomChat;
    }

    this.getFirebaseData().then(() => {
      this.disabledInput.set(false);
    });

    window.addEventListener('resize', this.handleResize);
    window.addEventListener('resize', this.handleChatContainerResize);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['clearTrigger']) {
      this.clearChatHistory();
    }
  }

  clearChatHistory() {
    this.chatHistory.set([]);
  }

  handleResize = () => {
    setTimeout(() => {
      const container = document.querySelector('.messages');
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 300);
  }

  handleChatContainerResize = () => {
    setTimeout(() => {
      if (this.chatContainerSpace?.nativeElement) {
        this.chatContainerSpace.nativeElement.scrollTop =
          this.chatContainerSpace.nativeElement.scrollHeight;
      }
    }, 300);
  };
  
  async getFirebaseData() {
    const workSpaceId = this.workSpaceClientId;
    if(workSpaceId) {
    
    const docRef = doc(this.firestore, `workspaces/${workSpaceId}`);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      applyBrandColors(data['brandColors']);
      document.querySelector('.chat-container-all')
      ?.classList.remove('hidden-until-theme');


      if(this.isChatSpace()) {
        this.isOpen = true;
        this.cd.detectChanges();
        const closeIcon = document.querySelector('.close-icon');
          closeIcon?.classList.add('hidden');

        if (this.chatContainerSpace) {
          this.chatContainerSpace.nativeElement.classList.add('full-screen');
        }
      }

      this.emails = data['users'].filter((x: any) => x.role === 'admin');
      this.userConfig.defaultTone = data['defaultTone'] || '';
      this.userConfig.rolePrompt = data['rolePrompt'] || '';
      this.userConfig.defaultInstructions = data['defaultInstructions'] || [];
      this.helpButtonText = data['helpButtonText'];
      this.talkToAdmin = data['talkToAdmin'];
    } else {
      console.error('El documento del usuario no existe.');
    }
  }

  }

  scrollToBottom() {
    const container = this.messagesContainer?.nativeElement;
    if (container) {
      setTimeout(() => {
        container.scrollTop = container.scrollTop + 50;
        // container.scrollTop = container.scrollHeight;
      }, 100);
    }
  }

  private loadFromLocalStorage() {
    const stored = localStorage.getItem(this.LOCAL_KEY);
    if (!stored) return false;

    try {
      const parsed = JSON.parse(stored);
      const isExpired = Date.now() - parsed.timestamp > this.LOCAL_TTL;

      if (isExpired) {
        localStorage.removeItem(this.LOCAL_KEY);
        return false;
      }

      // this.chatHistory.set(parsed.messages.map((m: any) => ({
      //   ...m,
      //   htmlContent: this.sanitizer.bypassSecurityTrustHtml(m.content)
      // })));

        if (parsed.email) {
          this.userEmailOrPhone = parsed.email || 'Usuario sin nombre';
          this.userIdentified.set(true);

        }
        // if (parsed.messages.length > 0) {
        //   this.userIdentified.set(true);
        // }
        // this.currentAssigned.set(parsed.assignedTo)
        this.conversationId = parsed.conversationId;
        return true;

    } catch (err) {
      console.error('Error leyendo Local Storage', err);
      return false;
    }
  }

  async sendQuery() {
    if(!this.userIdentified()) {
      this.loader = true;
      if(this.userEmailOrPhone !== '') {
        this.userIdentified.set(true);
        this.loader = false;
        await this.listenToChatHistory()
        .then((result: any) => {
          // console.log(result);
        });
      } else {
        this.snackBar.open('Debe tener un email o teléfono válido.', 'X', {
          duration: 3000
        });
        this.loader = false;
        return;
      }
    } else {

      const userText = this.userQuery.trim();
      if (!userText) return;
      
      const query = this.userQuery;
      const queryHtml = await this.parseMarkdown(query);

      this.chatHistory.update(history => [...history, 
        { role: 'user', 
          content: query,
          htmlContent: queryHtml,
          tokens: null,
          productos: []
        }]);

      this.isTyping.set(true);
      this.scrollToBottom();
      this.userQuery = '';

      if(this.workSpaceClientId && !this.isHumanChat()){
        this.chatService.queryChat(
          query, this.chatHistory(), 
          this.workSpaceClientId, 
          this.userConfig
        ).subscribe(
          async (res: any) => {
            if (res.answer) {
              const queryHtml = await this.parseMarkdown(res.answer.answer);

              this.chatHistory.update(history => [...history, 
                { 
                  role: 'assistant', 
                  content: res.answer.answer,
                  htmlContent: queryHtml,
                  tokens: res.answer.tokensUsed,
                  productos: res.answer.productos || [] 
                }]);

              this.userQuery = '';
              this.isTyping.set(false);
              this.scrollToBottom();

              if(!this.isTestMode) {
                await this.saveConversationToFirestoreInline(this.conversationId, this.chatHistory());
              }

              this.currentResponse.set(res.answer.answer);
            } else {
              this.currentResponse.set('No se encontró una respuesta.');
            }
          },
          (error) => {
            console.error('Error al consultar:', error);
            this.currentResponse.set('Hubo un error al consultar.');
          }
        );
      } 
      else if (this.workSpaceClientId && this.isHumanChat() && !this.isTestMode) {
        this.isTyping.set(false);
        this.saveConversationToFirestoreInline(this.conversationId, this.chatHistory());
      }
    }
  }

  async listenToChatHistory() {
    if (this.unsubscribeChat) return;
    const workSpaceId = this.workSpaceClientId;

    const convoCollection = collection(this.firestore, `workspaces/${workSpaceId}/conversations`);
    const q = query(convoCollection, where('conversationId', '==', this.conversationId));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const convoDocSnap = querySnapshot.docs[0];
      const convoDocRef = doc(this.firestore, `workspaces/${workSpaceId}/conversations/${convoDocSnap.id}`);
    // console.log('convoDocRef', convoDocRef);
    //     console.log('convoDocSnap', convoDocSnap);


      this.conversationId = convoDocSnap.data()['conversationId'];

      this.unsubscribeChat = onSnapshot(convoDocRef, async (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const mappedMessages = await Promise.all(
          data['messages'].map(async (msg: any) => ({
            ...msg,
            htmlContent: await this.parseMarkdown(msg.content),
          }))
        );

          this.chatHistory.set(mappedMessages);
          this.isTyping.set(false);
              
          if(data['humanRequest'] === true && data['assignedTo'] === null) {
            this.humanRequest.set(true);
            this.isHumanChat.set(false);
          }

          if(data['assignedTo'] !== null && data['humanRequest'] === false) {
            this.currentAssigned.set(data['assignedTo']);
            this.humanRequest.set(false);
            this.isHumanChat.set(true);
          }

          if(data['humanRequest'] === false && data['assignedTo'] === null) {
            this.humanRequest.set(false);
            this.isHumanChat.set(false);
          }

          // setTimeout(() => {
          //   const chatContainer = document.querySelector('.chat-container');
          //   if (chatContainer) {
          //     chatContainer.scrollTop = chatContainer.scrollHeight;
          //   }
          // }, 100);
        }
      });
    }
  }

  async saveConversationToFirestoreInline(
    conversationId: string,
    updatedHistory: { role: 'user' | 'assistant'; content: string }[]
    ) {
    let assignedTo: string | null = null;
    const workSpaceId = this.workSpaceClientId;
    const convoRef = doc(this.firestore, `workspaces/${workSpaceId}/conversations/${conversationId}`);
    const existingDoc = await getDoc(convoRef);

    if (existingDoc.exists()) {
      const existingData = existingDoc.data();
      assignedTo = existingData['assignedTo'] ?? null; // conserva si ya tiene algo
    }

    const existingData = existingDoc.data();
    const existingMessages = existingData ? existingData['messages'] : [];

    const isSame = JSON.stringify(existingMessages) === JSON.stringify(updatedHistory);
    if (isSame) return;

    const cleanedHistory = updatedHistory.map((msg: any) => ({
      role: msg.role,
      content: msg.content,
      tokens: msg.tokens ?? null,
      productos: msg.productos ?? []
    }));

    const updatedConversation: Conversation = {
      conversationId,
      updatedAt: Date.now(),
      messages: cleanedHistory,
      userEmail: this.userEmailOrPhone,
      assignedTo
    };

    await setDoc(convoRef, updatedConversation, { merge: true });
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
    setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
  }

  hideHelpButton() {
    this.showHelpButton.set(false);
  }

  cerrarApp() {
    if (window?.electronAPI?.closeApp) {
      window.electronAPI.closeApp();
    } else {
      console.warn('No está corriendo en Electron');
    }
  }

  isElectron(): boolean {
    return !!(window && window.process && window.process.versions && window.process.versions['electron']);
  }

  truncate(text: string, maxLength = 150): string {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  }

  toggleExpand(id: string) {
    this.isExpanded[id] = !this.isExpanded[id];
  }

  openRepresentativeDialog() {
    const dialogRef = this.dialog.open(this.representativeDialog);
    dialogRef.afterClosed().subscribe(result => {
      if (result === "true") {
        this.humanRequest.set(true);

      this.emails.forEach((x: any) => {
        const subjectMsj = 'Tienes una nueva notificación de atención.';
        this.sendEmail(x.email, subjectMsj);
      })
        this.chatService.updateConversationById(this.conversationId)
        .then((result: any) => {
          // console.log(result)
          return;
        })
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribeConversationListener?.();
    this.destroy.next(true);
        if (this.unsubscribeChat) {
      this.unsubscribeChat();
      this.unsubscribeChat = null;
    }
    window.removeEventListener('resize', this.handleResize);
     window.removeEventListener('resize', this.handleChatContainerResize);
  }

  ensureMaterialIconsLoaded() {
    const existing = document.querySelector('link[href*="fonts.googleapis.com/icon"]');
    if (!existing) {
      const link = document.createElement('link');
      link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
      link.rel = 'stylesheet';
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }
  }

  async parseMarkdown(md: string): Promise<SafeHtml> {
    const html = await marked.parse(md); // Esperamos el resultado si es Promise
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  sendEmail(email: string, subjectMsj: string) {
    const to = email;
    const subject = subjectMsj;
    const html = `
  <div style="font-family: Arial, sans-serif; max-width: 600px; margin: auto; padding: 20px; color: #333;">
    <h2 style="color: #2196f3;">Nueva solicitud de atención</h2>
    <p>Hola,</p>
    <p>El usuario <strong>${this.userEmailOrPhone}</strong> ha solicitado atención personalizada desde el chat.</p>
    <p>Por favor revisa el panel de administración para responder a la solicitud lo antes posible.</p>
    <hr style="margin: 20px 0;">
    <p style="font-size: 12px; color: #888;">
      Este es un mensaje automático, por favor no respondas a este correo.
    </p>
  </div>
`;

    this.emailService.sendEmail(to, subject, html).subscribe({
      next: res => console.log('✅ Enviado:',),
      error: err => console.error('❌ Error al enviar:',),
    });
  }

  getUserIpAndLocation() {
    fetch('https://ipapi.co/json/')
      .then(res => res.json())
      .then(data => {
        // console.log('IP:', data);
        // console.log('Lat/Lon:', data.latitude, data.longitude);

        // Guardar en tu backend junto con el inicio del chat
        // this.chatService.saveUserLocation({
        //   ip: data.ip,
        //   lat: data.latitude,
        //   lon: data.longitude
        // });
      })
      .catch(err => console.error(err));
  }

}
